import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";

import { HttpAuth, syncTokensAfterLogin } from "../../http";
import { userStorage } from "../../utils/storage";
import { AuthorizationResponse } from "../../types/user/userAuthModels";
import { ControlledTextField } from "../../components/forms/ControlledTextField";
import { ControlledTextPasswordFieldFilled } from "../../components/forms/ControlledTextPasswordField";
import { LG } from "../../styles/liquidGlass";
import { TEXTFIELD_STYLES } from "../../styles/textFieldStyles";

const schema = yup.object({
    mail: yup.string().email("Email inválido").required("El email es obligatorio"),
    password: yup.string().required("La contraseña es obligatoria"),
});

export default function LogIn() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const mailInicial = useMemo(() => searchParams.get("mail")?.trim() ?? "", [searchParams]);
    const mostrarExitoRestablecer = searchParams.get("restablecida") === "1";

    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(false);

    const { handleSubmit, control } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { mail: mailInicial, password: "" },
    });

    const mailActual = useWatch({ control, name: "mail" }) ?? "";

    const onSubmit = async (data: any) => {
        setError(undefined);
        setLoading(true);

        try {
            const response: AuthorizationResponse = await HttpAuth.authenticateUser(
                data.mail,
                data.password
            );

            if (response.tieneError) {
                setError(response.descripcionError);
                return;
            }

            userStorage.save(
                response.idUsuario,
                response.nombre,
                response.apellido,
                response.apellidoNombre,
                response.codUsuarioEstado,
                response.accessToken,
                response.refreshToken,
                response.idPerfil,
                response.codigoArea,
                response.telefono
            );

            syncTokensAfterLogin(response.accessToken, response.refreshToken);

            navigate("/");
        } catch (err: any) {
            setError(err.message ?? "Error desconocido");
        } finally {
            setLoading(false);
        }
    };

    const irARecupero = () => {
        const mail = String(mailActual ?? "").trim();
        navigate(
            `/recupero-contrasenia${mail ? `?mail=${encodeURIComponent(mail)}` : ""}`
        );
    };

    return (
        <Box sx={{ maxWidth: 420, mx: "auto", mt: 4, mb: 10 }}>
            <Box
                sx={{
                    p: 3,
                    background: LG.color.surfaceStrong,
                    backdropFilter: LG.blur,
                    WebkitBackdropFilter: LG.blur,
                    border: `1px solid ${LG.color.border}`,
                    borderRadius: LG.radius.lg,
                    boxShadow: LG.shadow.md,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 24,
                        fontWeight: 700,
                        fontFamily: "inherit",
                        color: LG.color.text,
                        mb: 3,
                    }}
                >
                    Iniciar sesión
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2.5}>
                        {mostrarExitoRestablecer && (
                            <Alert severity="success">
                                Contraseña actualizada correctamente. Ya podés iniciar sesión.
                            </Alert>
                        )}

                        <ControlledTextField control={control} name="mail" label="Email" />

                        <ControlledTextPasswordFieldFilled
                            control={control}
                            name="password"
                            label="Contraseña"
                            sx={TEXTFIELD_STYLES.standard}
                        />

                        <Button
                            type="button"
                            variant="text"
                            onClick={irARecupero}
                            sx={{
                                width: "fit-content",
                                minWidth: "auto",
                                p: 0,
                                mt: -1,
                                textTransform: "none",
                                color: LG.color.textSecondary,
                                fontFamily: "inherit",
                                "&:hover": { color: LG.color.text, background: "transparent" },
                            }}
                        >
                            Olvidé mi contraseña
                        </Button>

                        <Button
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            sx={{
                                background: LG.color.buttonDark,
                                color: LG.color.buttonDarkText,
                                borderRadius: LG.radius.pill,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                width: "100%",
                                "&:hover": { background: "#000" },
                                "&.Mui-disabled": { background: "rgba(0,0,0,0.2)", color: "rgba(255,255,255,0.5)" },
                            }}
                        >
                            {loading ? "Ingresando..." : "INGRESAR"}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => navigate("/")}
                            sx={{
                                background: LG.color.surface,
                                backdropFilter: LG.blurLight,
                                WebkitBackdropFilter: LG.blurLight,
                                border: `1px solid ${LG.color.border}`,
                                borderRadius: LG.radius.pill,
                                color: LG.color.text,
                                fontWeight: 600,
                                letterSpacing: "0.04em",
                                width: "100%",
                                "&:hover": {
                                    background: LG.color.surfaceHover,
                                    border: `1px solid ${LG.color.borderStrong}`,
                                },
                            }}
                        >
                            VOLVER
                        </Button>

                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </form>
            </Box>
        </Box>
    );
}
